import * as React from "react";
import { useTranslation } from "react-i18next";
import { Tab, TabList, TabPanel, Tabs } from "react-tabs";
import { addDays, startOfDay } from "rsuite/esm/internals/utils/date";

import style from "./view-tenant-details.scss";
import CopyToClipboard from "components/copy-to-clipboard/CopyToClipboard";
import { createLicenseTypes } from "components/licenses/common";
import { LoadingIndicator } from "components/loading-indicator/LoadingIndicator";
import StatusBadge, { Status } from "components/status-badge/StatusBadge";
import StaticTable from "components/support/api-guide/StaticTable";
import Heading from "components/typography/heading/Heading";
import { WarningBadge } from "components/warning/WarningBadge";
import { SUPPORT_EMAIL } from "domain/globalConstants";
import { EmsConfiguration, LicenseData } from "domain/licenses";
import { contractTypeToTranslationMap, countDaysTillDeletion, showDaysTillDeletion, Tenant } from "domain/tenants";
import { generateTenantTypeToTranslationMap, generateTierToTranslationMap } from "domain/tierRelatedMaps";
import { licenseService } from "services/licenses/LicenseService";
import { isUserParentInternal } from "services/tenants/tenantCookieService";
import { isStringNotBlank } from "utils/commonFunctions";
import { createDateLabel, formatDateWithoutTime, formatTimestamp } from "utils/format";

import testIds from "testIds.json";

export const ViewTenantDetails = (props: { activeTenant: Tenant }): JSX.Element => {
    const { t } = useTranslation();
    const { current: abortControllers } = React.useRef<AbortController[]>([]);
    const [loading, setLoading] = React.useState(false);
    const [licenses, setLicenses] = React.useState<LicenseData[]>([]);
    const [failedRequest, setFailedRequest] = React.useState<string>();
    const [licensesFailedRequest, setLicensesFailedRequest] = React.useState<string>();
    const [emsConfiguration, setEmsConfiguration] = React.useState<EmsConfiguration | null>(null);
    function createInformationSegment(
        testId: string,
        label: string,
        value?: string | number | React.ReactNode,
        labelWidth?: string
    ) {
        let deducedValue = value;
        if (typeof value === "undefined") {
            deducedValue = "-";
        } else {
            if (typeof value === "number") {
                deducedValue = value;
            } else if (typeof value === "string") {
                deducedValue = isStringNotBlank(value) ? value : "-";
            } else {
                deducedValue = value;
            }
        }
        return (
            <>
                <div className={style.label} style={labelWidth ? { width: labelWidth } : undefined}>
                    {label}
                </div>
                <div data-testid={testId}>{deducedValue}</div>
            </>
        );
    }
    function fetchLicenses() {
        if (licenses.length > 0) {
            return;
        }
        setLoading(true);
        const abortController = new AbortController();
        abortControllers.push(abortController);
        licenseService
            .fetchLicenses(abortController, undefined, false, props.activeTenant.uuid, undefined, false, true)
            .then((data) => {
                setLicenses(data.licenses);
            })
            .catch(() => {
                setLicensesFailedRequest(
                    t("CustomerProfile.licenseOverview.failedRequest", {
                        email: SUPPORT_EMAIL,
                    })
                );
            })
            .finally(() => {
                setLoading(false);
            });
    }
    function fetchEmsConfiguration() {
        if (emsConfiguration !== null) {
            return;
        }
        setLoading(true);
        const abortController = new AbortController();
        abortControllers.push(abortController);
        licenseService
            .fetchEmsConfiguration(props.activeTenant.uuid, abortController, props.activeTenant.region)
            .then((data) => {
                setEmsConfiguration(data);
            })
            .catch(() => {
                setFailedRequest(
                    t("CustomerProfile.emsConfiguration.failedRequest", {
                        email: SUPPORT_EMAIL,
                    })
                );
            })
            .finally(() => {
                setLoading(false);
            });
    }
    function createTableForUserStatistics() {
        const columns = [
            { value: t("CustomerProfile.usageStatistics.category"), className: style.licenseNameColumnWidth },
            { value: t("CustomerProfile.usageStatistics.ownCount") },
            { value: t("CustomerProfile.usageStatistics.overallCount") },
        ];
        const cells: (string | JSX.Element | null)[][] | undefined = [];

        cells.push([
            <div key="users">{t("CustomerProfile.usageStatistics.users")}</div>,
            <div key="users-own">{props.activeTenant.totalUsers}</div>,
            <div key="users-overall">{props.activeTenant.overallTotalUsers || 0}</div>,
        ]);

        cells.push([
            <div key="workflows">{t("CustomerProfile.usageStatistics.workflows")}</div>,
            <div key="workflows-own">{props.activeTenant.totalWorkflows}</div>,
            <div key="workflows-overall">{props.activeTenant.overallTotalWorkflows || 0}</div>,
        ]);

        cells.push([
            <div key="reports">{t("CustomerProfile.usageStatistics.reports")}</div>,
            <div key="reports-own">{props.activeTenant.totalReportsCount || 0}</div>,
            <div key="reports-overall">{props.activeTenant.overallTotalReportsCount || 0}</div>,
        ]);

        cells.push([
            <div key="subtenants">{t("CustomerProfile.usageStatistics.subtenants")}</div>,
            <div key="subtenants-own">{props.activeTenant.subTenantsCount}</div>,
            <div key="subtenants-overall">{props.activeTenant.overallSubTenantsCount || 0}</div>,
        ]);

        cells.push([
            <div key="loginCount">{t("CustomerProfile.usageStatistics.loginCount")}</div>,
            <div key="loginCount-own">{props.activeTenant.totalLogins || 0}</div>,
            <div key="loginCount-overall">{props.activeTenant.overallTotalLogins || 0}</div>,
        ]);

        return (
            <>
                <div>
                    <StaticTable
                        headers={columns}
                        cells={cells}
                        tableClass={style.licenseTableWidth}
                        testId={testIds.workArea.tenant.viewTenantDialog.usageStatistics.table}
                    ></StaticTable>
                </div>
            </>
        );
    }

    function createTableForAvailableLicenses() {
        const columns = [
            { value: t("CustomerProfile.licenseOverview.product"), className: style.licenseNameColumnWidth },
            { value: t("CustomerProfile.licenseOverview.ownBalance") },
            { value: t("CustomerProfile.licenseOverview.balance") },
        ];
        const cells: (string | JSX.Element | null)[][] | undefined = [];
        const blanccoProducts = createLicenseTypes(true);
        if (licenses.length > 0) {
            const sortedProducts = licenses
                .map((each) => {
                    const foundProduct = blanccoProducts.find((product) => product.productId === each.type);
                    return {
                        productName: foundProduct ? foundProduct.productName : each.license,
                        ownBalance: each.overallRemaining,
                        recursiveBalance: each.recursiveBalance,
                    };
                })
                .sort((a, b) => a.productName.localeCompare(b.productName));
            sortedProducts.forEach((each) => {
                cells.push([
                    <div key={each.productName}>{each.productName}</div>,
                    <div key={each.productName}>{each.ownBalance}</div>,
                    <div key={each.productName} className={style.label}>
                        {each.recursiveBalance ?? " - "}
                    </div>,
                ]);
            });
            return (
                <>
                    <div className={style.standardModalTitle}>{t("CustomerProfile.licenseOverview.introduction")}</div>
                    <div>
                        <StaticTable
                            headers={columns}
                            cells={cells}
                            tableClass={style.licenseTableWidth}
                            testId={testIds.workArea.tenant.viewTenantDialog.licenseOverview.table}
                        ></StaticTable>
                    </div>
                </>
            );
        } else {
            if (licensesFailedRequest) {
                return <div className={style.standardModalTitle}>{licensesFailedRequest}</div>;
            }
            return (
                <div className={style.standardModalTitle}>
                    {t("Licenses.emptyStateMessage", { email: SUPPORT_EMAIL })}
                </div>
            );
        }
    }
    /* TODO BCC-4838 make into a global function */
    const calculateTrialDaysLeft = () => {
        const tenantExpirationDate = props.activeTenant.expirationDate;
        if (!tenantExpirationDate) {
            return null;
        }

        const trialEndDate = new Date(tenantExpirationDate);
        const today = new Date();

        trialEndDate.setUTCHours(19, 59, 59, 999);
        today.setUTCHours(19, 59, 59, 999);

        const calculateDifference = trialEndDate.getTime() - today.getTime();

        const daysLeft = Math.ceil(calculateDifference / (1000 * 60 * 60 * 24));

        return daysLeft;
    };

    const createTrialstatusBadge = () => {
        const trialDaysLeft = calculateTrialDaysLeft();

        const contractType = props.activeTenant.contractType;
        if (contractType === "TRIAL") {
            const trialStatusBadge =
                trialDaysLeft !== null && trialDaysLeft > 0 ? (
                    <StatusBadge
                        values={[
                            {
                                status: Status.WARNING,
                                title: t("CustomerProfile.trialTenantsNavPanel.trialDaysLeft", { days: trialDaysLeft }),
                            },
                        ]}
                    />
                ) : (
                    <StatusBadge
                        values={[
                            {
                                status: Status.NEUTRAL,
                                title: t("CustomerProfile.trialTenantsNavPanel.trialEndedText"),
                            },
                        ]}
                    />
                );

            return <>{trialStatusBadge}</>;
        } else {
            return contractTypeToTranslationMap(t).get(props.activeTenant.contractType);
        }
    };

    function createSettingsSection() {
        if (!emsConfiguration) {
            return (
                <div className={style.standardModalTitle}>{t("CustomerProfile.emsConfiguration.noConfiguration")}</div>
            );
        }
        const columnWidth = "200px";
        return (
            <>
                <div className={style.tenantProfile}>
                    {createInformationSegment(
                        testIds.workArea.tenant.viewTenantDialog.settings.emsIdLabel,
                        t("CustomerProfile.emsConfiguration.accountId"),
                        emsConfiguration.emsId,
                        columnWidth
                    )}
                    {createInformationSegment(
                        testIds.workArea.tenant.viewTenantDialog.settings.hlEntitlementsLabel,
                        t("CustomerProfile.emsConfiguration.hlEntitlements"),
                        emsConfiguration.hlEntitlements ? t("CustomerProfile.enabled") : t("CustomerProfile.disabled"),
                        columnWidth
                    )}
                    {createInformationSegment(
                        testIds.workArea.tenant.viewTenantDialog.settings.slEntitlementsLabel,
                        t("CustomerProfile.emsConfiguration.slEntitlements"),
                        emsConfiguration.slEntitlements ? t("CustomerProfile.enabled") : t("CustomerProfile.disabled"),
                        columnWidth
                    )}
                    {createInformationSegment(
                        testIds.workArea.tenant.viewTenantDialog.settings.availableSlActivationsLabel,
                        t("CustomerProfile.emsConfiguration.availableSlActivations"),
                        emsConfiguration.availableSlActivations,
                        columnWidth
                    )}
                    {createInformationSegment(
                        testIds.workArea.tenant.viewTenantDialog.settings.usedSlActivationsLabel,
                        t("CustomerProfile.emsConfiguration.usedSlActivations"),
                        emsConfiguration.usedSlActivations,
                        columnWidth
                    )}
                </div>
            </>
        );
    }

    return (
        <Tabs>
            <TabList>
                <Tab data-testid={testIds.workArea.tenant.viewTenantDialog.generic.tab}>
                    {t("CustomerProfile.tab.tenantDetails")}
                </Tab>
                {isUserParentInternal() && (
                    <Tab
                        data-testid={testIds.workArea.tenant.viewTenantDialog.settings.tab}
                        onClick={() => {
                            fetchEmsConfiguration();
                        }}
                    >
                        {t("CustomerProfile.tab.entitlementSettings")}
                    </Tab>
                )}
                <Tab
                    data-testid={testIds.workArea.tenant.viewTenantDialog.licenseOverview.tab}
                    onClick={() => {
                        fetchLicenses();
                    }}
                >
                    {t("CustomerProfile.tab.licenseOverview")}
                </Tab>
            </TabList>
            <TabPanel>
                <Heading tag="div" variant="SUBTITLE_1" className={style.standardModalTitle}>
                    {t("CustomerProfile.genericDetail")}
                </Heading>
                <div className={style.tenantProfile}>
                    <div className={style.label}>{t("CustomerProfile.customerIdentifier")}</div>
                    <div data-testid={testIds.workArea.tenant.viewTenantDialog.generic.uuidLabel}>
                        {props.activeTenant.uuid}
                        <CopyToClipboard value={props.activeTenant.uuid} />
                    </div>
                    {createInformationSegment(
                        testIds.workArea.tenant.viewTenantDialog.generic.contractTypeLabel,
                        t("AddCustomerForm.usecaseType"),
                        createTrialstatusBadge()
                    )}
                    {isUserParentInternal() &&
                        props.activeTenant.contractType === "TRIAL" &&
                        createInformationSegment(
                            testIds.workArea.tenant.viewTenantDialog.generic.automaticDeletionLabel,
                            t("CustomerProfile.automaticDeletion"),
                            props.activeTenant.automaticDeletion
                                ? t("CustomerProfile.enabled")
                                : t("CustomerProfile.disabled")
                        )}
                    {createInformationSegment(
                        testIds.workArea.tenant.viewTenantDialog.generic.tenantTypeLabel,
                        t("AddCustomerForm.tenantType"),
                        generateTenantTypeToTranslationMap(t).get(props.activeTenant.type)
                    )}
                    {createInformationSegment(
                        testIds.workArea.tenant.viewTenantDialog.generic.regionLabel,
                        t("CustomerProfile.region"),
                        props.activeTenant.region
                    )}
                    {createInformationSegment(
                        testIds.workArea.tenant.viewTenantDialog.generic.countryLabel,
                        t("CustomerProfile.country"),
                        props.activeTenant.countryCode
                    )}
                    {createInformationSegment(
                        testIds.workArea.tenant.viewTenantDialog.generic.createdDateLabel,
                        t("CustomerProfile.createdDate"),
                        createDateLabel(props.activeTenant.createdTimestamp)
                    )}
                    {createInformationSegment(
                        testIds.workArea.tenant.viewTenantDialog.generic.createdByLabel,
                        t("CustomerProfile.createdBy"),
                        props.activeTenant.createdBy
                    )}

                    {createInformationSegment(
                        testIds.workArea.tenant.viewTenantDialog.generic.tierLabel,
                        t("AddCustomerForm.blanccoBundles"),
                        generateTierToTranslationMap(t).get(props.activeTenant.tier)
                    )}
                    {isUserParentInternal() &&
                        createInformationSegment(
                            testIds.workArea.tenant.viewTenantDialog.generic.drawbackPreventionLabel,
                            t("CustomerProfile.drawbackPrevention"),
                            props.activeTenant.drawbackPrevention
                                ? t("CustomerProfile.enabled")
                                : t("CustomerProfile.disabled")
                        )}
                    <div className={style.label}>{t("CustomerProfile.notes")}</div>
                    <div data-testid={testIds.workArea.tenant.viewTenantDialog.generic.notesLabel}>
                        {props.activeTenant.notes !== "" ? (
                            props.activeTenant.notes
                        ) : (
                            <div className={style.emptyField}>{t("CustomerProfile.noNotesAvailable")}</div>
                        )}
                    </div>
                    <div className={style.label}>{t("CustomerProfile.usageStatistics.lastLoginUser")}</div>
                    <div data-testid={testIds.workArea.tenant.viewTenantDialog.generic.lastLoginUserLabel}>
                        {props.activeTenant.lastLoggedInUser || "-"}
                    </div>
                    <div className={style.label}>{t("CustomerProfile.usageStatistics.lastLoginDate")}</div>
                    <div data-testid={testIds.workArea.tenant.viewTenantDialog.generic.lastLoginDateLabel}>
                        {props.activeTenant.lastLogin
                            ? formatTimestamp(new Date(props.activeTenant.lastLogin).toString())
                            : "-"}
                    </div>
                </div>
                <Heading tag="div" variant="SUBTITLE_1" className={style.standardModalTitle}>
                    {t("CustomerProfile.contactDetails")}
                </Heading>
                <div className={style.tenantProfile}>
                    <div className={style.label}>{t("CustomerProfile.mainContactName")}</div>
                    <div data-testid={testIds.workArea.tenant.viewTenantDialog.contact.mainContactNameLabel}>
                        {props.activeTenant.contactName !== "" ? (
                            props.activeTenant.contactName
                        ) : (
                            <div className={style.emptyField}>{t("Common.notProvided")}</div>
                        )}
                    </div>

                    <div className={style.label}>{t("CustomerProfile.email")}</div>
                    <div data-testid={testIds.workArea.tenant.viewTenantDialog.contact.emailLabel}>
                        {props.activeTenant.contactEmail !== "" ? (
                            props.activeTenant.contactEmail
                        ) : (
                            <div className={style.emptyField}>{t("Common.notProvided")}</div>
                        )}
                        {props.activeTenant.contactEmail !== "" && (
                            <CopyToClipboard value={props.activeTenant.contactEmail} />
                        )}
                    </div>
                </div>
                <Heading tag="div" variant="SUBTITLE_1" className={style.standardModalTitle}>
                    {t("CustomerProfile.statusSettings")}
                </Heading>
                <div className={style.tenantProfile}>
                    <div className={style.label}>{t("CustomerProfile.status")}</div>
                    <StatusBadge
                        testId={testIds.workArea.tenant.viewTenantDialog.statusSettings.statusLabel}
                        values={[
                            {
                                status: props.activeTenant.status
                                    ? Status.SUCCESS
                                    : !showDaysTillDeletion(
                                            props.activeTenant.contractType,
                                            props.activeTenant.automaticDeletion
                                        )
                                      ? Status.NEUTRAL
                                      : Status.ERROR,
                                title: props.activeTenant.status
                                    ? t("Common.active")
                                    : !showDaysTillDeletion(
                                            props.activeTenant.contractType,
                                            props.activeTenant.automaticDeletion
                                        )
                                      ? t("Common.inactive")
                                      : t("CustomersTable.daysTillDeletion", {
                                            daysTillDeletion: countDaysTillDeletion(props.activeTenant.expirationDate),
                                        }),
                            },
                        ]}
                        tooltip={true}
                    />
                    <div className={style.label}>{t("Common.accessExpiry")}:</div>
                    <div data-testid={testIds.workArea.tenant.viewTenantDialog.statusSettings.expirationDateLabel}>
                        {props.activeTenant.expirationDate == "" || props.activeTenant.expirationDate == undefined
                            ? t("Common.never")
                            : createDateLabel(formatDateWithoutTime(props.activeTenant.expirationDate))}
                    </div>
                </div>
                {isUserParentInternal() && (
                    <>
                        <Heading tag="div" variant="SUBTITLE_1" className={style.standardModalTitle}>
                            {t("CustomerProfile.salesForceAccount")}
                        </Heading>
                        <div className={style.tenantProfile}>
                            {createInformationSegment(
                                testIds.workArea.tenant.viewTenantDialog.salesForceAccount.salesForceAccountIdLabel,
                                t("CustomerProfile.salesForceAccountId"),
                                props.activeTenant.salesforceAccountId
                            )}
                            {createInformationSegment(
                                testIds.workArea.tenant.viewTenantDialog.salesForceAccount.leadIdLabel,
                                t("CustomerProfile.leadId"),
                                props.activeTenant.salesforceLeadId
                            )}
                            {createInformationSegment(
                                testIds.workArea.tenant.viewTenantDialog.salesForceAccount.contractIdLabel,
                                t("CustomerProfile.contractId"),
                                props.activeTenant.salesforceContractId
                            )}
                            {createInformationSegment(
                                testIds.workArea.tenant.viewTenantDialog.salesForceAccount.contractStateLabel,
                                t("CustomerProfile.contractState"),
                                props.activeTenant.salesforceContractState
                            )}
                            {createInformationSegment(
                                testIds.workArea.tenant.viewTenantDialog.salesForceAccount.contractEmailLabel,
                                t("CustomerProfile.contractEmail"),
                                props.activeTenant.salesforceContactEmail
                            )}
                        </div>
                    </>
                )}
                <Heading tag={"div"} variant="SUBTITLE_1" className={style.standardModalTitle}>
                    {t("CustomerProfile.usageStatistics.title")}
                </Heading>
                <div>
                    <WarningBadge
                        note={t("CustomerProfile.usageStatistics.note", {
                            date: formatDateWithoutTime(startOfDay(addDays(new Date(), -1)).toString()),
                        })}
                    />
                </div>
                <div className={style.standardModalTitle}>{t("CustomerProfile.usageStatistics.tableDescription")}</div>
                {createTableForUserStatistics()}
            </TabPanel>
            {isUserParentInternal() && (
                <TabPanel>{loading ? <LoadingIndicator /> : failedRequest || createSettingsSection()}</TabPanel>
            )}
            <TabPanel>
                {loading ? <LoadingIndicator /> : licensesFailedRequest || createTableForAvailableLicenses()}
            </TabPanel>
        </Tabs>
    );
};
